import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container, Row, Col, Card, CardBody, CardHeader, Form,
  Label, Input, Button, Nav, NavItem, NavLink, TabContent, TabPane,
  UncontrolledAlert, Progress, UncontrolledDropdown, DropdownToggle,
  DropdownMenu, DropdownItem
} from "reactstrap";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import CountUp from "react-countup";
import { usePocketBase } from "../../../PocketBaseContext";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

import Dropzone from "react-dropzone";
import CustomerQuotes from './CustomerInvoices';
import CustomerPersonnel from './CustomerPersonnel';
import CustomerEvents from './CustomerEvents';
import FileManager from './FileManager';

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const pb = usePocketBase();
  const [activeTab, setActiveTab] = useState("1");
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalEvents: 0,
    totalReports: 0,
    lastActivity: null
  });
  const [alert, setAlert] = useState({ show: false, color: "", message: "" });

  const validationSchema = Yup.object({
    name: Yup.string().required("Müşteri adı gereklidir"),
    kurumsallik: Yup.string().required("Müşteri tipi gereklidir"),
    tax_number: Yup.string().when("kurumsallik", {
      is: "kurumsal",
      then: (schema) => schema.required("Vergi numarası gereklidir")
    }),
    tax_office: Yup.string().when("kurumsallik", {
      is: "kurumsal",
      then: (schema) => schema.required("Vergi dairesi gereklidir")
    }),
    tc_number: Yup.string().when("kurumsallik", {
      is: "bireysel",
      then: (schema) => schema.required("T.C. Kimlik numarası gereklidir").length(11, "T.C. Kimlik numarası 11 haneli olmalıdır")
    }),
    nationality: Yup.string(),
    birth_date: Yup.date().nullable(),
    gender: Yup.string(),
    customer_address: Yup.string().required("Adres gereklidir"),
    customer_brand_description: Yup.string(),
    customer_contact: Yup.array()
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      kurumsallik: "",
      tax_number: "",
      tax_office: "",
      tc_number: "",
      birth_date: "",
      nationality: "",
      gender: "",
      customer_address: "",
      customer_brand_description: "",
      customer_contact: [],
      pictures: [],
      bakim_raporlari: [],
      servis_formlari: [],
      fatura: []
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await pb.collection('customers').update(id, values);
        setAlert({
          show: true,
          color: "success",
          message: "Müşteri başarıyla güncellendi"
        });
        setIsEditing(false);
      } catch (error) {
        setAlert({
          show: true,
          color: "danger",
          message: "Güncelleme sırasında bir hata oluştu"
        });
      }
    },
  });

  // index.js içindeki useEffect hook'unu basitleştirelim
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);

        // Sadece müşteri detayını çek
        const customerData = await pb.collection('customers').getOne(id);
        console.log('Fetched customer data:', customerData);

        // Formik değerlerini güncelle
        const formikValues = {
          name: customerData.name || '',
          kurumsallik: customerData.kurumsallik || '',
          tax_number: customerData.tax_number?.toString() || '',
          tax_office: customerData.tax_office || '',
          tc_number: customerData.tc_number || '',
          birth_date: customerData.birth_date || '',
          nationality: customerData.nationality || '',
          gender: customerData.gender || '',
          customer_address: customerData.customer_address || '',
          customer_brand_description: customerData.customer_brand_description || '',
          customer_contact: customerData.customer_contact || [],
          pictures: customerData.pictures || [],
          bakim_raporlari: customerData.bakim_raporlari || [],
          servis_formlari: customerData.servis_formlari || [],
          fatura: customerData.fatura || []
        };

        formik.setValues(formikValues);

        // İstatistikleri güncelle
        setStats({
          totalInvoices: customerData.fatura?.length || 0,
          totalEvents: 0, // Bu bilgi response'da yoksa 0 olarak bırak
          totalReports: (customerData.bakim_raporlari?.length || 0) +
            (customerData.servis_formlari?.length || 0),
          lastActivity: customerData.updated
        });

        setLoading(false);
      } catch (error) {
        console.error("Veri yüklenirken hata:", error);
        setAlert({
          show: true,
          color: "danger",
          message: "Veriler yüklenirken bir hata oluştu: " + error.message
        });
        setLoading(false);
      }
    };

    fetchData();
  }, [id, pb]);

  const handleFileUpload = async (result, field) => {
    try {
      if (result) {
        // Update formik state with the new files
        formik.setFieldValue(field, result[field]);
        
        setAlert({
          show: true,
          color: "success",
          message: "Files uploaded successfully"
        });
      }
    } catch (error) {
      console.error('File upload error:', error);
      setAlert({
        show: true,
        color: "danger",
        message: "Error during file upload: " + error.message
      });
    }
  };

  const handleDeleteFile = async (field, filename) => {
    try {
      const updatedFiles = formik.values[field].filter(f => f !== filename);
      const updateData = { [field]: updatedFiles };
      await pb.collection('customers').update(id, updateData);

      formik.setFieldValue(field, updatedFiles);
      setAlert({
        show: true,
        color: "success",
        message: "Dosya başarıyla silindi"
      });
    } catch (error) {
      setAlert({
        show: true,
        color: "danger",
        message: "Dosya silinirken hata oluştu"
      });
    }
  };

  const renderInput = (name, label, type = "text", options = null) => {
    const invalid = formik.touched[name] && formik.errors[name];
    const value = formik.values[name];

    if (!isEditing) {
      return (
        <div className="mb-3">
          <Label>{label}</Label>
          <div className="form-control bg-light">
            {type === "select" ? (
              options?.find(opt => opt.value === value)?.label
            ) : type === "date" ? (
              value ? new Date(value).toLocaleDateString('tr-TR') : '-'
            ) : (
              value || '-'
            )}
          </div>
        </div>
      );
    }

    // Input type'a göre render
    switch (type) {
      case "select":
        return (
          <div className="mb-3">
            <Label htmlFor={name}>{label}</Label>
            <Select
              id={name}
              name={name}
              value={options?.find(opt => opt.value === value)}
              onChange={(option) => formik.setFieldValue(name, option.value)}
              options={options}
              className={invalid ? 'is-invalid' : ''}
            />
            {invalid && (
              <div className="invalid-feedback d-block">{formik.errors[name]}</div>
            )}
          </div>
        );

      case "textarea":
        return (
          <div className="mb-3">
            <Label htmlFor={name}>{label}</Label>
            <Input
              id={name}
              name={name}
              type={type}
              rows="4"
              value={value || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={invalid}
              disabled={!isEditing}
            />
            {invalid && (
              <div className="invalid-feedback">{formik.errors[name]}</div>
            )}
          </div>
        );

      case "date":
        return (
          <div className="mb-3">
            <Label htmlFor={name}>{label}</Label>
            <Input
              id={name}
              name={name}
              type="date"
              value={value ? value.split('T')[0] : ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={invalid}
              disabled={!isEditing}
            />
            {invalid && (
              <div className="invalid-feedback">{formik.errors[name]}</div>
            )}
          </div>
        );

      default:
        return (
          <div className="mb-3">
            <Label htmlFor={name}>{label}</Label>
            <Input
              id={name}
              name={name}
              type={type}
              value={value || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={invalid}
              disabled={!isEditing}
            />
            {invalid && (
              <div className="invalid-feedback">{formik.errors[name]}</div>
            )}
          </div>
        );
    }
  };

  const renderFiles = (field, files) => {
    if (!files?.length) {
      return (
        <div className="text-center text-muted py-4">
          <p className="mb-0">Henüz dosya yüklenmemiş</p>
        </div>
      );
    }

    return (
      <div className="list-group mt-4">
        {files.map((file, index) => (
          <div key={index} className="list-group-item list-group-item-action">
            <div className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <i className="ri-file-text-line fs-17 align-middle"></i>
              </div>
              <div className="flex-grow-1 ms-2">
                <h6 className="mb-1">{file}</h6>
                <small className="text-muted">
                  Yüklenme Tarihi: {new Date().toLocaleDateString()}
                </small>
              </div>
              {isEditing && (
                <div className="flex-shrink-0">
                  <UncontrolledDropdown>
                    <DropdownToggle tag="button" className="btn btn-icon btn-sm btn-ghost-secondary">
                      <i className="ri-more-2-fill"></i>
                    </DropdownToggle>
                    <DropdownMenu end>
                      <DropdownItem href={pb.getFileUrl({ collectionId: 'customers', id: id, filename: file })}>
                        <i className="ri-eye-fill align-bottom me-2 text-muted"></i>Görüntüle
                      </DropdownItem>
                      <DropdownItem href={pb.getFileUrl({ collectionId: 'customers', id: id, filename: file })} download>
                        <i className="ri-download-2-fill align-bottom me-2 text-muted"></i>İndir
                      </DropdownItem>
                      <DropdownItem divider />
                      <DropdownItem className="text-danger" onClick={() => handleDeleteFile(field, file)}>
                        <i className="ri-delete-bin-fill align-bottom me-2 text-danger"></i>Sil
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <div className="spinner-border text-primary m-1" role="status">
              <span className="sr-only">Yükleniyor...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={formik.values.name} pageTitle="Müşteri Detayı" />

        {alert.show && (
          <UncontrolledAlert color={alert.color} toggle={() => setAlert({ show: false })}>
            {alert.message}
          </UncontrolledAlert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="border-0 align-items-center d-flex">
                <h4 className="card-title mb-0 flex-grow-1">Müşteri Özeti</h4>
                <div className="d-flex gap-1">
                  {isEditing ? (
                    <>
                      <Button color="success" size="sm" onClick={formik.handleSubmit}>
                        <i className="ri-save-line align-bottom me-1"></i> Kaydet
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                          setIsEditing(false);
                          formik.resetForm();
                        }}
                      >
                        <i className="ri-close-line align-bottom me-1"></i> İptal
                      </Button>
                    </>
                  ) : (
                    <Button color="primary" size="sm" onClick={() => setIsEditing(true)}>
                      <i className="ri-edit-line align-bottom me-1"></i> Düzenle
                    </Button>
                  )}
                  <Button color="light" size="sm" onClick={() => navigate("/reform-Customers/list")}>
                    <i className="ri-arrow-left-line align-bottom me-1"></i> Geri
                  </Button>
                </div>
              </CardHeader>

              <CardHeader className="p-0 border-0 bg-light-subtle">
                <Row className="g-0 text-center">
                  <Col xs={6} sm={3}>
                    <div className="p-3 border border-dashed border-start-0">
                      <h5 className="mb-1">
                        <CountUp start={0} end={stats.totalInvoices} duration={2} />
                      </h5>
                      <p className="text-muted mb-0">Toplam Fatura</p>
                    </div>
                  </Col>
                  <Col xs={6} sm={3}>
                    <div className="p-3 border border-dashed border-start-0">
                      <h5 className="mb-1">
                        <CountUp start={0} end={stats.totalEvents} duration={2} />
                      </h5>
                      <p className="text-muted mb-0">Aktif Etkinlikler</p>
                    </div>
                  </Col>
                  <Col xs={6} sm={3}>
                    <div className="p-3 border border-dashed border-start-0">
                      <h5 className="mb-1">
                        <CountUp start={0} end={stats.totalReports} duration={2} />
                      </h5>
                      <p className="text-muted mb-0">Toplam Rapor</p>
                    </div>
                  </Col>
                  <Col xs={6} sm={3}>
                    <div className="p-3 border border-dashed border-start-0 border-end-0">
                      <h5 className="mb-1">
                        {stats.lastActivity ? new Date(stats.lastActivity).toLocaleDateString() : '-'}
                      </h5>
                      <p className="text-muted mb-0">Son Aktivite</p>
                    </div>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Nav tabs className="nav-tabs-custom nav-primary">
                  <NavItem>
                    <NavLink
                      style={{ cursor: "pointer" }}
                      className={activeTab === "1" ? "active" : ""}
                      onClick={() => setActiveTab("1")}
                    >
                      <i className="ri-user-line align-middle me-1"></i>
                      Genel Bilgiler
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      style={{ cursor: "pointer" }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="ri-attachment-line align-middle me-1"></i>
                      Dosyalar
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      style={{ cursor: "pointer" }}
                      className={activeTab === "3" ? "active" : ""}
                      onClick={() => setActiveTab("3")}
                    >
                      <i className="ri-file-list-line align-middle me-1"></i>
                      Raporlar
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      style={{ cursor: "pointer" }}
                      className={activeTab === "4" ? "active" : ""}
                      onClick={() => setActiveTab("4")}
                    >
                      <i className="ri-calendar-2-line align-middle me-1"></i>
                      Etkinlikler
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      style={{ cursor: "pointer" }}
                      className={activeTab === "5" ? "active" : ""}
                      onClick={() => setActiveTab("5")}
                    >
                      <i className="ri-money-dollar-circle-line align-middle me-1"></i>
                      Teklifler
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="pt-4">
                  <TabPane tabId="1">
                    <Row>
                      <Col lg={8}>
                        <Form>
                          <Row>
                            <Col md={6}>
                              {renderInput("name", "Müşteri Adı")}
                            </Col>
                            <Col md={6}>
                              {renderInput("kurumsallik", "Müşteri Tipi", "select", [
                                { value: "kurumsal", label: "Kurumsal" },
                                { value: "bireysel", label: "Bireysel" }
                              ])}
                            </Col>
                          </Row>

                          {formik.values.kurumsallik === 'kurumsal' && (
                            <Row>
                              <Col md={6}>
                                {renderInput("tax_number", "Vergi Numarası")}
                              </Col>
                              <Col md={6}>
                                {renderInput("tax_office", "Vergi Dairesi")}
                              </Col>
                            </Row>
                          )}

                          {formik.values.kurumsallik === 'bireysel' && (
                            <>
                              <Row>
                                <Col md={6}>
                                  {renderInput("tc_number", "T.C. Kimlik No")}
                                </Col>
                                <Col md={6}>
                                  {renderInput("birth_date", "Doğum Tarihi", "date")}
                                </Col>
                              </Row>
                              <Row>
                                <Col md={6}>
                                  {renderInput("nationality", "Uyruk")}
                                </Col>
                                <Col md={6}>
                                  {renderInput("gender", "Cinsiyet", "select", [
                                    { value: "Erkek", label: "Erkek" },
                                    { value: "Kadın", label: "Kadın" },
                                    { value: "Diğer", label: "Diğer" }
                                  ])}
                                </Col>
                              </Row>
                            </>
                          )}

                          <Row>
                            <Col md={12}>
                              {renderInput("customer_address", "Adres", "textarea")}
                            </Col>
                          </Row>

                          <Row>
                            <Col md={12}>
                              {renderInput("customer_brand_description", "Açıklama", "textarea")}
                            </Col>
                          </Row>
                        </Form>
                      </Col>
                      <Col lg={4}>
                        <CustomerPersonnel pb={pb} customerId={id} isEditing={isEditing} />
                      </Col>
                    </Row>
                  </TabPane>

                  <TabPane tabId="2">
  <FileManager
    title="Müşteri Dosyaları"
    files={formik.values.pictures}
    field="pictures"
    isEditing={isEditing}
    pb={pb}
    recordId={id}
    onUpload={handleFileUpload}
    onDelete={handleDeleteFile}
    allowedFileTypes={['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx']}
    maxFileSize={5 * 1024 * 1024} // 5MB
    dropzoneText="Dosyaları buraya sürükleyin veya yüklemek için tıklayın"
  />
</TabPane>

<TabPane tabId="3">
  <Row>
    <Col md={6}>
      <FileManager
        title="Bakım Raporları"
        files={formik.values.bakim_raporlari}
        field="bakim_raporlari"
        isEditing={isEditing}
        pb={pb}
        recordId={id}
        onUpload={handleFileUpload}
        onDelete={handleDeleteFile}
        allowedFileTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx']}
        maxFileSize={10 * 1024 * 1024} // 10MB
        dropzoneText="Bakım raporlarını yükleyin"
      />
    </Col>

    <Col md={6}>
      <FileManager
        title="Servis Formları"
        files={formik.values.servis_formlari}
        field="servis_formlari"
        isEditing={isEditing}
        pb={pb}
        recordId={id}
        onUpload={handleFileUpload}
        onDelete={handleDeleteFile}
        allowedFileTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx']}
        maxFileSize={10 * 1024 * 1024} // 10MB
        dropzoneText="Servis formlarını yükleyin"
      />
    </Col>
  </Row>

  {/* İstatistikler bölümü */}
  <Row className="mt-4">
    <Col lg={12}>
      <h5 className="card-title mb-4">Rapor İstatistikleri</h5>
      <div>
        <div className="d-flex mb-4">
          <div className="flex-grow-1">
            <h6 className="mb-0">Bakım Raporları</h6>
            <p className="text-muted mb-2">{formik.values.bakim_raporlari?.length || 0} adet</p>
          </div>
          <div className="flex-shrink-0">
            <Progress
              value={formik.values.bakim_raporlari?.length || 0}
              max={10}
              color="primary"
              style={{ height: "6px", width: "120px" }}
            />
          </div>
        </div>
        <div className="d-flex mb-4">
          <div className="flex-grow-1">
            <h6 className="mb-0">Servis Formları</h6>
            <p className="text-muted mb-2">{formik.values.servis_formlari?.length || 0} adet</p>
          </div>
          <div className="flex-shrink-0">
            <Progress
              value={formik.values.servis_formlari?.length || 0}
              max={10}
              color="success"
              style={{ height: "6px", width: "120px" }}
            />
          </div>
        </div>
      </div>
    </Col>
  </Row>
</TabPane>

                  <TabPane tabId="4">
                    <Row>
                      <Col>
                        <CustomerEvents pb={pb} customerId={id} />
                      </Col>
                    </Row>
                  </TabPane>

                  <TabPane tabId="5">
                    <Row>
                      <Col>
                        <CustomerQuotes pb={pb} customerId={id} />
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CustomerDetails;